// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import styles from '@app/features/channel/components/pickers/gif/ImportFavoriteGifsModal.module.css';
import FavoriteGif from '@app/features/expressions/state/FavoriteGif';
import FavoriteGifImport from '@app/features/expressions/state/FavoriteGifImport';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Textarea} from '@app/features/ui/components/form/Input';
import {MAX_FAVORITE_GIFS} from '@app/vendor/constants/LimitConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useState} from 'react';
export const ImportFavoriteGifsModal = observer(function ImportFavoriteGifsModal() {
	const {t} = useLingui();
	const [text, setText] = useState('');
	const handleClose = useCallback(() => {
		ModalCommands.pop();
	}, []);
	const handleImport = useCallback(async () => {
		if (FavoriteGifImport.isRunning) return;
		await FavoriteGifImport.startImport(text);
	}, [text]);
	const handleCancel = useCallback(() => {
		FavoriteGifImport.cancelImport();
	}, []);
	const handleReset = useCallback(() => {
		FavoriteGifImport.reset();
		setText('');
	}, []);
	const remaining = MAX_FAVORITE_GIFS - FavoriteGif.totalCount;
	const isAtLimit = remaining <= 0;
	return (
		<Modal.Root size="small">
			<Modal.Header title={t`Import Favorite GIFs`} onClose={handleClose} />
			<Modal.Content>
				{FavoriteGifImport.isDone ? (
					<div className={styles.body}>
						<p className={styles.description}>
							{FavoriteGifImport.skippedCount > 0 ? (
								FavoriteGifImport.importedCount === 1 ? (
									<Trans>
										Import complete. Added 1 GIF, skipped {FavoriteGifImport.skippedCount} (duplicates or limit
										reached).
									</Trans>
								) : (
									<Trans>
										Import complete. Added {FavoriteGifImport.importedCount} GIFs, skipped{' '}
										{FavoriteGifImport.skippedCount} (duplicates or limit reached).
									</Trans>
								)
							) : FavoriteGifImport.importedCount === 1 ? (
								<Trans>Import complete. Added 1 GIF.</Trans>
							) : (
								<Trans>Import complete. Added {FavoriteGifImport.importedCount} GIFs.</Trans>
							)}
						</p>
					</div>
				) : FavoriteGifImport.isRunning ? (
					<div className={styles.body}>
						<p className={styles.description}>
							<Trans>
								Importing… {FavoriteGifImport.processedCount} / {FavoriteGifImport.totalToImport} processed,{' '}
								{FavoriteGifImport.importedCount} added.
							</Trans>
						</p>
						<div className={styles.progressTrack}>
							<div className={styles.progressBar} style={{width: `${Math.round(FavoriteGifImport.progress * 100)}%`}} />
						</div>
						<p className={styles.muted}>
							<Trans>You can close this modal — the import will continue in the background.</Trans>
						</p>
					</div>
				) : (
					<div className={styles.body}>
						<p className={styles.description}>
							{remaining === 1 ? (
								<Trans>
									Paste GIF URLs below, one per line. You can add up to {remaining.toLocaleString()} more GIF (limit:{' '}
									{MAX_FAVORITE_GIFS.toLocaleString()}).
								</Trans>
							) : (
								<Trans>
									Paste GIF URLs below, one per line. You can add up to {remaining.toLocaleString()} more GIFs (limit:{' '}
									{MAX_FAVORITE_GIFS.toLocaleString()}).
								</Trans>
							)}
						</p>
						{isAtLimit ? (
							<p className={styles.danger}>
								<Trans>
									You've reached the maximum number of favorite GIFs ({MAX_FAVORITE_GIFS.toLocaleString()}).
								</Trans>
							</p>
						) : (
							<Textarea
								label={t`GIF URLs`}
								value={text}
								onChange={(e) => setText(e.target.value)}
								placeholder={'https://example.com/gif1.gif\nhttps://example.com/gif2.gif'}
								minRows={8}
								maxRows={14}
								spellCheck={false}
								autoCapitalize="off"
								autoCorrect="off"
								autoComplete="off"
							/>
						)}
					</div>
				)}
			</Modal.Content>
			<Modal.Footer>
				{FavoriteGifImport.isDone ? (
					<>
						<Button onClick={handleReset} variant="secondary">
							<Trans>Import More</Trans>
						</Button>
						<Button onClick={handleClose} variant="primary">
							<Trans>Done</Trans>
						</Button>
					</>
				) : FavoriteGifImport.isRunning ? (
					<>
						<Button onClick={handleClose} variant="secondary">
							<Trans>Close</Trans>
						</Button>
						<Button onClick={handleCancel} variant="danger-secondary">
							<Trans>Cancel Import</Trans>
						</Button>
					</>
				) : (
					<>
						<Button onClick={handleClose} variant="secondary">
							<Trans>Cancel</Trans>
						</Button>
						<Button onClick={handleImport} variant="primary" disabled={!text.trim() || isAtLimit}>
							<Trans>Import</Trans>
						</Button>
					</>
				)}
			</Modal.Footer>
		</Modal.Root>
	);
});
